import { configureStore, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { useDispatch, useSelector, type TypedUseSelectorHook } from 'react-redux';
import { api } from './baseApi';

interface Toast {
  id: number;
  kind: 'success' | 'error' | 'info';
  message: string;
}

interface UiState {
  toasts: Toast[];
  commandOpen: boolean;
  sessionExpired: boolean;
}

const ui = createSlice({
  name: 'ui',
  initialState: { toasts: [], commandOpen: false, sessionExpired: false } as UiState,
  reducers: {
    pushToast: (state, action: PayloadAction<Omit<Toast, 'id'>>) => {
      state.toasts.push({ ...action.payload, id: Date.now() + Math.random() });
    },
    dismissToast: (state, action: PayloadAction<number>) => {
      state.toasts = state.toasts.filter((t) => t.id !== action.payload);
    },
    setCommandOpen: (state, action: PayloadAction<boolean>) => {
      state.commandOpen = action.payload;
    }
  },
  extraReducers: (builder) => {
    builder.addCase('auth/sessionExpired', (state) => {
      state.sessionExpired = true;
    });
  }
});

export const { pushToast, dismissToast, setCommandOpen } = ui.actions;

export const store = configureStore({
  reducer: { [api.reducerPath]: api.reducer, ui: ui.reducer },
  middleware: (getDefault) => getDefault().concat(api.middleware)
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
export const useAppDispatch: () => AppDispatch = useDispatch;
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
